import {
  Box,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import React, { useCallback, useState } from 'react'

import { Alert } from '@mui/material'
import { CsvExportCursorBased } from '../../../ui/CsvExportCursorBased'
import InfiniteScroll from 'react-infinite-scroller'
import { Link } from 'react-router-dom'
import { TruncatedId } from '../../truncated-id'
import { useListDeliveredNotifications } from '../hooks/useListDeliveredNotifications'
import { useParams } from 'react-router'

const LIMIT = 100

export const ListDelivered = () => {
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const { scheduledId } = useParams<{ scheduledId: string }>()
  const { data, loading, error, fetchMore } = useListDeliveredNotifications({
    id: scheduledId,
    cursor: null,
    limit: LIMIT
  })

  const loadMore = useCallback(async () => {
    if (isLoadingMore) {
      return
    }

    setIsLoadingMore(true)
    return await fetchMore({
      updateQuery(previousQueryResult: any, options) {
        const newSearch =
          options.fetchMoreResult?.scheduledNotification.__typename === 'ScheduledNotificationV2'
            ? options.fetchMoreResult.scheduledNotification.listNotifications.notifications
            : []
        const previousSearch =
          previousQueryResult.scheduledNotification.__typename === 'ScheduledNotificationV2'
            ? previousQueryResult.scheduledNotification.listNotifications.notifications
            : []
        const possiblyDuplicatedNotifications = [...previousSearch, ...newSearch]

        const seen = new Set()
        const filteredNotifications = possiblyDuplicatedNotifications.filter((notification: any) => {
          const duplicate = seen.has(notification.id)
          seen.add(notification.id)
          return !duplicate
        })
        const notifications = filteredNotifications

        return {
          scheduledNotification: {
            ...previousQueryResult.scheduledNotification,
            listNotifications: {
              ...previousQueryResult.scheduledNotification.listNotifications,
              nextCursor:
                options.fetchMoreResult?.scheduledNotification.__typename === 'ScheduledNotificationV2'
                  ? options.fetchMoreResult?.scheduledNotification.listNotifications.nextCursor
                  : null,
              notifications
            }
          }
        }
      },
      variables: {
        limit: LIMIT,
        id: scheduledId,
        cursor:
          data?.scheduledNotification.__typename === 'ScheduledNotificationV2'
            ? data?.scheduledNotification.listNotifications.nextCursor
            : null
      }
    }).then(res => {
      setIsLoadingMore(false)
      return res
    })
  }, [isLoadingMore, fetchMore, scheduledId, data])

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <FlexHorizontal>
          <MuiLink component={Link} to='/notifications'>
            <Typography variant='h4'>Notifications</Typography>
          </MuiLink>
          <Typography variant='h4'>&nbsp;/&nbsp;</Typography>
          <MuiLink component={Link} to={`/notifications/${scheduledId}`}>
            <Typography variant='h4'>Notification</Typography>
          </MuiLink>
          <Typography variant='h4'>&nbsp;/ Delivered</Typography>
        </FlexHorizontal>
        <CsvExportCursorBased
          query={useListDeliveredNotifications.QUERY}
          getVariables={cursor => ({
            limit: 1000,
            id: scheduledId,
            cursor: cursor || null
          })}
          formatData={({ data }) => {
            return (data?.scheduledNotification.listNotifications?.notifications || []).map(
              (item: { id: string; personId: string }) => ({
                id: item.id,
                personId: item.personId
              })
            )
          }}
          getCursor={({ data }) => {
            return data?.scheduledNotification.listNotifications?.nextCursor || null
          }}
        />
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      {loading && !data?.scheduledNotification ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity='error'>There was an error loading the notifications</Alert>
      ) : data?.scheduledNotification.__typename !== 'ScheduledNotificationV2' ||
        !data?.scheduledNotification?.listNotifications?.notifications?.length ? (
        <Alert severity='info'>There are no notifications</Alert>
      ) : (
        <>
          {data ? (
            <InfiniteScroll
              pageStart={0}
              loadMore={loadMore}
              hasMore={!!data?.scheduledNotification?.listNotifications?.nextCursor}
              loader={
                <Box>
                  <Spacer size='large' />
                  <CircularProgress />
                </Box>
              }>
              <TableContainer
                component={Paper}
                style={{
                  maxWidth: 'calc(100vw - 290px)',
                  overflowX: 'auto'
                }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>PersonID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.scheduledNotification.listNotifications.notifications.map(notification => {
                      return (
                        <TableRow key={notification.personId}>
                          <TableCell>
                            <TruncatedId value={notification.id} full />
                          </TableCell>
                          <TableCell>
                            <TruncatedId
                              value={notification.personId}
                              path={`/notifications/subscriber/${notification.personId}`}
                              full
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          ) : null}
        </>
      )}
    </Box>
  )
}
